/**
 * Tag color assignment utilities
 *
 * Existing tags are persisted and should never be recomputed from mutable UI palettes.
 * New tags use deterministic, generated colors with luminance guardrails.
 */

export const TAG_COLOR_ASSIGNMENT_VERSION = 13;

const GOLDEN_ANGLE_DEGREES = 137.508;
const DEFAULT_SATURATION = 68;
const DEFAULT_LIGHTNESS = 42;
const MAX_LIGHTNESS = 70;
const MIN_LIGHTNESS = 20;
const LIGHTNESS_STEP = 2;

export const TAG_COLOR_MIN_LUMINANCE = 0.09;
export const TAG_COLOR_MAX_LUMINANCE = 0.16;
export const TAG_COLOR_MAX_GENERATION_ATTEMPTS = 720;
export const TAG_COLOR_TARGET_MIN_HUE_DISTANCE = 22;
export const TAG_COLOR_MIN_HUE_DISTANCE_FLOOR = 6;
export const TAG_COLOR_TARGET_MIN_RGB_DISTANCE = 48;
export const TAG_COLOR_MIN_RGB_DISTANCE_FLOOR = 10;
export const TAG_COLOR_HUE_BUCKET_SIZE = 30;
export const TAG_COLOR_FAMILY_BUCKET_SIZE = 60;
export const TAG_COLOR_CANDIDATE_SCAN_LIMIT = 720;
export const TAG_COLOR_EXCLUDED_HUE_RANGES = Object.freeze([
    // Avoid olive/yellow-lime zone only — the range that genuinely looks
    // like muddy/feces color at our luminance level. Orange (30-49°) and
    // red-orange (8-29°) are vivid and acceptable, so they remain open.
    Object.freeze({ start: 50, end: 91 })
]);
const TAG_COLOR_FAMILIES = Object.freeze([
    Object.freeze({ id: 'red-orange', start: 8, end: 29 }),
    Object.freeze({ id: 'orange', start: 30, end: 49 }),
    // 50-90° excluded (olive/yellow-lime)
    Object.freeze({ id: 'green', start: 91, end: 140 }),
    Object.freeze({ id: 'teal', start: 141, end: 190 }),
    Object.freeze({ id: 'blue', start: 191, end: 235 }),
    Object.freeze({ id: 'indigo', start: 236, end: 270 }),
    Object.freeze({ id: 'violet', start: 271, end: 300 }),
    Object.freeze({ id: 'pink', start: 301, end: 340 }),
    Object.freeze({ id: 'red', start: 341, end: 7 })
]);
export const EMPTY_TAG_COLOR = '#64748b';
export const TAG_TEXT_COLOR_DARK = '#0f172a';
export const TAG_TEXT_COLOR_LIGHT = '#ffffff';

function clamp(value, min, max) {
    return Math.min(max, Math.max(min, value));
}

function normalizeHue(hue) {
    const normalized = hue % 360;
    return normalized < 0 ? normalized + 360 : normalized;
}

function isHueExcluded(hue) {
    const normalized = normalizeHue(hue);
    return TAG_COLOR_EXCLUDED_HUE_RANGES.some((range) => {
        if (!range || !Number.isFinite(range.start) || !Number.isFinite(range.end)) {
            return false;
        }
        const start = normalizeHue(range.start);
        const end = normalizeHue(range.end);
        if (start <= end) {
            return normalized >= start && normalized <= end;
        }
        return normalized >= start || normalized <= end;
    });
}

function isHueInRange(hue, start, end) {
    const normalized = normalizeHue(hue);
    const normalizedStart = normalizeHue(start);
    const normalizedEnd = normalizeHue(end);
    if (normalizedStart <= normalizedEnd) {
        return normalized >= normalizedStart && normalized <= normalizedEnd;
    }
    return normalized >= normalizedStart || normalized <= normalizedEnd;
}

function getFamilyIdForHue(hue) {
    const family = TAG_COLOR_FAMILIES.find((entry) => isHueInRange(hue, entry.start, entry.end));
    return family?.id || 'other';
}

function hslToRgb(h, s, l) {
    const hue = normalizeHue(h) / 360;
    const saturation = clamp(s, 0, 100) / 100;
    const lightness = clamp(l, 0, 100) / 100;

    if (saturation === 0) {
        const gray = Math.round(lightness * 255);
        return [gray, gray, gray];
    }

    const hueToRgb = (p, q, t) => {
        let adjusted = t;
        if (adjusted < 0) adjusted += 1;
        if (adjusted > 1) adjusted -= 1;
        if (adjusted < (1 / 6)) return p + (q - p) * 6 * adjusted;
        if (adjusted < (1 / 2)) return q;
        if (adjusted < (2 / 3)) return p + (q - p) * ((2 / 3) - adjusted) * 6;
        return p;
    };

    const q = lightness < 0.5
        ? lightness * (1 + saturation)
        : lightness + saturation - (lightness * saturation);
    const p = 2 * lightness - q;

    const red = Math.round(hueToRgb(p, q, hue + (1 / 3)) * 255);
    const green = Math.round(hueToRgb(p, q, hue) * 255);
    const blue = Math.round(hueToRgb(p, q, hue - (1 / 3)) * 255);

    return [red, green, blue];
}

function rgbToHsl(red, green, blue) {
    const r = clamp(red, 0, 255) / 255;
    const g = clamp(green, 0, 255) / 255;
    const b = clamp(blue, 0, 255) / 255;
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    const delta = max - min;
    const lightness = (max + min) / 2;

    let hue = 0;
    let saturation = 0;

    if (delta !== 0) {
        saturation = delta / (1 - Math.abs((2 * lightness) - 1));
        switch (max) {
            case r:
                hue = ((g - b) / delta) % 6;
                break;
            case g:
                hue = ((b - r) / delta) + 2;
                break;
            default:
                hue = ((r - g) / delta) + 4;
                break;
        }
        hue *= 60;
    }

    return {
        h: normalizeHue(hue),
        s: saturation * 100,
        l: lightness * 100
    };
}

function rgbToHex(red, green, blue) {
    const toHex = (value) => clamp(value, 0, 255).toString(16).padStart(2, '0');
    return `#${toHex(red)}${toHex(green)}${toHex(blue)}`;
}

function hexToRgb(hexColor) {
    const normalized = String(hexColor || '').trim().replace(/^#/, '');
    const longHex = normalized.length === 3
        ? normalized.split('').map((char) => char + char).join('')
        : normalized;

    if (!/^[0-9a-fA-F]{6}$/.test(longHex)) {
        return null;
    }

    return {
        r: parseInt(longHex.slice(0, 2), 16),
        g: parseInt(longHex.slice(2, 4), 16),
        b: parseInt(longHex.slice(4, 6), 16)
    };
}

function getRelativeLuminanceFromRgb(red, green, blue) {
    const normalized = [red, green, blue].map((channel) => channel / 255);
    const linear = normalized.map((value) => {
        if (value <= 0.03928) return value / 12.92;
        return ((value + 0.055) / 1.055) ** 2.4;
    });

    return (0.2126 * linear[0]) + (0.7152 * linear[1]) + (0.0722 * linear[2]);
}

function getRelativeLuminanceFromHsl(h, s, l) {
    const [red, green, blue] = hslToRgb(h, s, l);
    return getRelativeLuminanceFromRgb(red, green, blue);
}

function getGuardedLightness(hue, saturation, baseLightness) {
    let lightness = clamp(baseLightness, MIN_LIGHTNESS, MAX_LIGHTNESS);
    let luminance = getRelativeLuminanceFromHsl(hue, saturation, lightness);

    while (luminance < TAG_COLOR_MIN_LUMINANCE && lightness < MAX_LIGHTNESS) {
        lightness = Math.min(MAX_LIGHTNESS, lightness + LIGHTNESS_STEP);
        luminance = getRelativeLuminanceFromHsl(hue, saturation, lightness);
    }

    while (luminance > TAG_COLOR_MAX_LUMINANCE && lightness > MIN_LIGHTNESS) {
        lightness = Math.max(MIN_LIGHTNESS, lightness - LIGHTNESS_STEP);
        luminance = getRelativeLuminanceFromHsl(hue, saturation, lightness);
    }

    return lightness;
}

function getGeneratedColorCandidateForKey(tagKey, step = 0) {
    const safeKey = String(tagKey || '');
    const hash = hashString(safeKey);
    const saturationHash = hashString(`${safeKey}::s`);
    const lightnessHash = hashString(`${safeKey}::l`);
    const baseHue = hash % 360;
    const hue = normalizeHue(baseHue + (step * GOLDEN_ANGLE_DEGREES));
    const saturationBase = 58 + (saturationHash % 23); // 58-80
    const saturationStepShift = ((step % 5) - 2) * 4;  // -8, -4, 0, +4, +8
    const saturation = clamp(saturationBase + saturationStepShift, 52, 86);
    const lightnessBase = 34 + (lightnessHash % 13); // 34-46
    const lightness = getGuardedLightness(hue, saturation, lightnessBase || DEFAULT_LIGHTNESS);
    const [red, green, blue] = hslToRgb(hue, saturation, lightness);
    return {
        color: rgbToHex(red, green, blue),
        rgb: [red, green, blue],
        hue,
        saturation,
        lightness
    };
}

function getGeneratedAllowedColorCandidateForKey(tagKey, allowedStep = 0) {
    const targetAllowedStep = Math.max(0, Number(allowedStep) || 0);
    let seenAllowed = 0;

    for (let rawStep = 0; rawStep < TAG_COLOR_MAX_GENERATION_ATTEMPTS; rawStep++) {
        const candidate = getGeneratedColorCandidateForKey(tagKey, rawStep);
        if (isHueExcluded(candidate.hue)) {
            continue;
        }

        if (seenAllowed === targetAllowedStep) {
            return candidate;
        }

        seenAllowed++;
    }

    return getGeneratedColorCandidateForKey(tagKey, 0);
}

function getHueDistance(a, b) {
    const delta = Math.abs(normalizeHue(a) - normalizeHue(b));
    return Math.min(delta, 360 - delta);
}

function getMinHueDistance(candidateHue, usedHues) {
    if (!Array.isArray(usedHues) || usedHues.length === 0) {
        return 360;
    }

    let minDistance = 360;
    for (let i = 0; i < usedHues.length; i++) {
        minDistance = Math.min(minDistance, getHueDistance(candidateHue, usedHues[i]));
    }
    return minDistance;
}

function getHueFromColor(color) {
    const rgb = hexToRgb(color);
    if (!rgb) return null;
    const hsl = rgbToHsl(rgb.r, rgb.g, rgb.b);
    return Number.isFinite(hsl.h) ? hsl.h : null;
}

function getRequiredHueDistance(usedColorCount) {
    const count = Number.isFinite(Number(usedColorCount)) ? Number(usedColorCount) : 0;
    const spacingBasedDistance = Math.floor((360 / Math.max(1, count + 1)) * 0.8);
    return clamp(
        spacingBasedDistance,
        TAG_COLOR_MIN_HUE_DISTANCE_FLOOR,
        TAG_COLOR_TARGET_MIN_HUE_DISTANCE
    );
}

function getRequiredRgbDistance(usedColorCount) {
    const count = Number.isFinite(Number(usedColorCount)) ? Number(usedColorCount) : 0;
    const densityBasedDistance = Math.floor(TAG_COLOR_TARGET_MIN_RGB_DISTANCE - (count * 0.34));
    return clamp(
        densityBasedDistance,
        TAG_COLOR_MIN_RGB_DISTANCE_FLOOR,
        TAG_COLOR_TARGET_MIN_RGB_DISTANCE
    );
}

function getRgbDistance(a, b) {
    const aRgb = Array.isArray(a) && a.length >= 3 ? a : [0, 0, 0];
    const bRgb = Array.isArray(b) && b.length >= 3 ? b : [0, 0, 0];
    const dr = aRgb[0] - bRgb[0];
    const dg = aRgb[1] - bRgb[1];
    const db = aRgb[2] - bRgb[2];
    return Math.sqrt((dr * dr) + (dg * dg) + (db * db));
}

function getMinRgbDistance(candidateRgb, usedRgbs) {
    const list = Array.isArray(usedRgbs) ? usedRgbs : [];
    if (list.length === 0) {
        return 441.673; // sqrt(255^2 * 3), max RGB distance
    }

    let minDistance = Infinity;
    for (let i = 0; i < list.length; i++) {
        minDistance = Math.min(minDistance, getRgbDistance(candidateRgb, list[i]));
    }
    return minDistance;
}

function getHueBucketIndex(hue) {
    const bucketCount = Math.max(1, Math.floor(360 / TAG_COLOR_HUE_BUCKET_SIZE));
    return Math.floor(normalizeHue(hue) / TAG_COLOR_HUE_BUCKET_SIZE) % bucketCount;
}

function getFamilyBucketIndex(hue) {
    const bucketCount = Math.max(1, Math.floor(360 / TAG_COLOR_FAMILY_BUCKET_SIZE));
    return Math.floor(normalizeHue(hue) / TAG_COLOR_FAMILY_BUCKET_SIZE) % bucketCount;
}

function buildHueBucketCounts(usedHues) {
    const counts = new Map();
    const hues = Array.isArray(usedHues) ? usedHues : [];
    for (let i = 0; i < hues.length; i++) {
        const bucket = getHueBucketIndex(hues[i]);
        counts.set(bucket, (counts.get(bucket) || 0) + 1);
    }
    return counts;
}

function getAdjacentBucketLoad(bucketIndex, bucketCounts) {
    const bucketCount = Math.max(1, Math.floor(360 / TAG_COLOR_HUE_BUCKET_SIZE));
    const prev = (bucketIndex - 1 + bucketCount) % bucketCount;
    const next = (bucketIndex + 1) % bucketCount;
    return (bucketCounts.get(prev) || 0) + (bucketCounts.get(next) || 0);
}

function getFamilyBucketLoad(bucketIndex, bucketCounts) {
    const normalized = Number.isFinite(Number(bucketIndex)) ? Number(bucketIndex) : 0;
    return bucketCounts.get(normalized) || 0;
}

function getMaxHueBucketLoad(usedColorCount) {
    const count = Number.isFinite(Number(usedColorCount)) ? Number(usedColorCount) : 0;
    if (count <= 24) return 3;
    if (count <= 60) return 5;
    return 8;
}

function getMaxFamilyBucketLoad(usedColorCount) {
    const count = Number.isFinite(Number(usedColorCount)) ? Number(usedColorCount) : 0;
    if (count <= 30) return 6;
    if (count <= 70) return 11;
    return 16;
}

function getFamilyCap(usedColorCount) {
    const count = Number.isFinite(Number(usedColorCount)) ? Number(usedColorCount) : 0;
    const base = Math.ceil((count + 1) / TAG_COLOR_FAMILIES.length);
    if (count < 28) return Math.max(2, base);
    if (count < 56) return Math.max(3, base + 1);
    return Math.max(4, base + 2);
}

function getHardHueDistanceFloor(usedColorCount) {
    const count = Number.isFinite(Number(usedColorCount)) ? Number(usedColorCount) : 0;
    if (count < 30) return 12;
    if (count < 60) return 8;
    return 4;
}

function getHardRgbDistanceFloor(usedColorCount) {
    const count = Number.isFinite(Number(usedColorCount)) ? Number(usedColorCount) : 0;
    if (count < 30) return 24;
    if (count < 60) return 16;
    return 10;
}

export function hashString(str) {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
        const char = str.charCodeAt(i);
        hash = ((hash << 5) - hash) + char;
        hash = hash & hash; // Convert to 32-bit integer
    }
    return Math.abs(hash);
}

export function normalizeTagColorKey(tagName) {
    return String(tagName || "").trim().toLowerCase();
}

export function getFallbackTagColorForKey(tagKey, step = 0) {
    if (!tagKey) {
        return EMPTY_TAG_COLOR;
    }
    return getGeneratedAllowedColorCandidateForKey(tagKey, step).color;
}

export function getTagTextColorForBackground(backgroundColor) {
    return TAG_TEXT_COLOR_LIGHT;
}

export function getPreferredTagColorForKey(tagKey, tagColorMap) {
    if (!tagKey) {
        return EMPTY_TAG_COLOR;
    }

    const colorMap = tagColorMap && typeof tagColorMap === 'object' ? tagColorMap : {};
    const usedColors = new Set(
        Object.values(colorMap)
            .filter(Boolean)
            .map((color) => String(color).trim().toLowerCase())
    );
    const usedHues = Array.from(usedColors)
        .map((color) => getHueFromColor(color))
        .filter((hue) => Number.isFinite(hue));
    const usedRgbs = Array.from(usedColors)
        .map((color) => hexToRgb(color))
        .filter((rgb) => rgb && Number.isFinite(rgb.r) && Number.isFinite(rgb.g) && Number.isFinite(rgb.b))
        .map((rgb) => [rgb.r, rgb.g, rgb.b]);
    const requiredHueDistance = getRequiredHueDistance(usedHues.length);
    const requiredRgbDistance = getRequiredRgbDistance(usedHues.length);
    const hardHueFloor = getHardHueDistanceFloor(usedColors.size);
    const hardRgbFloor = getHardRgbDistanceFloor(usedColors.size);
    const familyCap = getFamilyCap(usedColors.size);
    const maxHueBucketLoad = getMaxHueBucketLoad(usedColors.size);
    const maxFamilyBucketLoad = getMaxFamilyBucketLoad(usedColors.size);
    const hueBucketCounts = buildHueBucketCounts(usedHues);
    const familyCounts = new Map();
    for (let i = 0; i < usedHues.length; i++) {
        const familyId = getFamilyIdForHue(usedHues[i]);
        familyCounts.set(familyId, (familyCounts.get(familyId) || 0) + 1);
    }
    const familyBucketCounts = new Map();
    for (let i = 0; i < usedHues.length; i++) {
        const familyBucket = getFamilyBucketIndex(usedHues[i]);
        familyBucketCounts.set(familyBucket, (familyBucketCounts.get(familyBucket) || 0) + 1);
    }
    const scanLimit = Math.min(TAG_COLOR_MAX_GENERATION_ATTEMPTS, TAG_COLOR_CANDIDATE_SCAN_LIMIT);
    let bestCandidate = null;

    const evaluateCandidate = (candidate, stepIndex) => {
        const normalizedCandidate = candidate.color.toLowerCase();
        if (usedColors.has(normalizedCandidate)) {
            return null;
        }

        const minDistance = getMinHueDistance(candidate.hue, usedHues);
        const minRgbDistance = getMinRgbDistance(candidate.rgb, usedRgbs);
        const bucket = getHueBucketIndex(candidate.hue);
        const bucketLoad = hueBucketCounts.get(bucket) || 0;
        const adjacentBucketLoad = getAdjacentBucketLoad(bucket, hueBucketCounts);
        const familyId = getFamilyIdForHue(candidate.hue);
        const familyLoad = familyCounts.get(familyId) || 0;
        const exceedsFamilyCap = familyLoad >= familyCap;
        const familyBucket = getFamilyBucketIndex(candidate.hue);
        const familyBucketLoad = getFamilyBucketLoad(familyBucket, familyBucketCounts);
        const exceedsHueBucket = bucketLoad >= maxHueBucketLoad;
        const exceedsFamilyBucket = familyBucketLoad >= maxFamilyBucketLoad;
        const meetsRequiredDistance = minDistance >= requiredHueDistance;
        const meetsRequiredRgbDistance = minRgbDistance >= requiredRgbDistance;
        const score =
            (meetsRequiredDistance ? 40_000 : 0) +
            (meetsRequiredRgbDistance ? 40_000 : 0) +
            (minDistance * 280) +
            (minRgbDistance * 1800) -
            (bucketLoad * 30) -
            (adjacentBucketLoad * 18) -
            (familyLoad * 26) -
            (exceedsFamilyCap ? 180_000 : 0) -
            (familyBucketLoad * 24) -
            (exceedsHueBucket ? 120_000 : 0) -
            (exceedsFamilyBucket ? 70_000 : 0) -
            (stepIndex * 0.01);

        return {
            color: candidate.color,
            score,
            minDistance,
            minRgbDistance
        };
    };

    // Pass 1: strict visual jumps for practical tag counts.
    for (let i = 0; i < scanLimit; i++) {
        const candidate = getGeneratedColorCandidateForKey(tagKey, i);
        if (isHueExcluded(candidate.hue)) {
            continue;
        }

        const evaluated = evaluateCandidate(candidate, i);
        if (!evaluated) {
            continue;
        }

        if (evaluated.minDistance < hardHueFloor || evaluated.minRgbDistance < hardRgbFloor) {
            continue;
        }

        if (!bestCandidate || evaluated.score > bestCandidate.score) {
            bestCandidate = evaluated;
        }
    }

    // Pass 2: softer floors — still prevent near-identical results but relax hue clustering.
    if (!bestCandidate) {
        const softRgbFloor = 7; // minimum perceptually meaningful distance
        const softHueFloor = Math.max(2, Math.floor(hardHueFloor / 3));
        for (let i = 0; i < scanLimit; i++) {
            const candidate = getGeneratedColorCandidateForKey(tagKey, i);
            if (isHueExcluded(candidate.hue)) {
                continue;
            }

            const evaluated = evaluateCandidate(candidate, i);
            if (!evaluated) {
                continue;
            }

            if (evaluated.minRgbDistance < softRgbFloor || evaluated.minDistance < softHueFloor) {
                continue;
            }

            if (!bestCandidate || evaluated.score > bestCandidate.score) {
                bestCandidate = evaluated;
            }
        }
    }

    // Pass 3: absolute fallback — pick best available regardless of distance floors.
    if (!bestCandidate) {
        for (let i = 0; i < scanLimit; i++) {
            const candidate = getGeneratedColorCandidateForKey(tagKey, i);
            if (isHueExcluded(candidate.hue)) {
                continue;
            }

            const evaluated = evaluateCandidate(candidate, i);
            if (!evaluated) {
                continue;
            }

            if (!bestCandidate || evaluated.score > bestCandidate.score) {
                bestCandidate = evaluated;
            }
        }
    }

    if (bestCandidate) {
        return bestCandidate.color;
    }

    return getGeneratedAllowedColorCandidateForKey(tagKey, 0).color;
}

export function ensureTagColor(tagName, tagColorMap) {
    const key = normalizeTagColorKey(tagName);

    if (!key) {
        return { key, color: EMPTY_TAG_COLOR, changed: false };
    }

    if (tagColorMap[key]) {
        return { key, color: tagColorMap[key], changed: false };
    }

    const color = getPreferredTagColorForKey(key, tagColorMap);
    tagColorMap[key] = color;
    return { key, color, changed: true };
}

export function buildTagColorMapFromTasks(tasks, tagColorMap = {}) {
    let changed = false;
    const taskList = Array.isArray(tasks) ? tasks : [];
    const uniqueKeys = new Set();

    taskList.forEach((task) => {
        const tags = Array.isArray(task?.tags) ? task.tags : [];
        tags.forEach((tag) => {
            const key = normalizeTagColorKey(tag);
            if (key) uniqueKeys.add(key);
        });
    });

    Array.from(uniqueKeys)
        .sort((a, b) => a.localeCompare(b))
        .forEach((key) => {
            if (tagColorMap[key]) return;
            const color = getPreferredTagColorForKey(key, tagColorMap);
            tagColorMap[key] = color;
            changed = true;
        });

    return { tagColorMap, changed };
}

/**
 * Inline style for the tinted tag look used across the app: a soft wash of the
 * tag's fixed color behind text in a stronger shade of the same color. The
 * background mixes with transparent so it reads on any surface (light or dark).
 * @param {string} color - The tag's fixed color (from getTagColor)
 * @returns {string} CSS declarations for a style="" attribute
 */
export function getTintedTagStyle(color) {
    return `background-color: color-mix(in srgb, ${color} 16%, transparent); color: color-mix(in srgb, ${color} 65%, var(--text-primary));`;
}

/**
 * Apply the tinted tag look to an existing element (DOM-built badges).
 * @param {HTMLElement} el
 * @param {string} color - The tag's fixed color (from getTagColor)
 */
export function applyTintedTagStyle(el, color) {
    el.style.backgroundColor = `color-mix(in srgb, ${color} 16%, transparent)`;
    el.style.color = `color-mix(in srgb, ${color} 65%, var(--text-primary))`;
}